import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Play, Zap, Sliders, CheckCircle2, ArrowRight, ArrowLeft, Bell, Building2, Info, HelpCircle } from "lucide-react";

const STORAGE_KEY = "bf_autoagent_tour_v1";

const STEPS = [
  {
    Icon: Bell,
    tone: "amber",
    title: "1. Seed a Search Alert first",
    body: "The Auto-Agent only scans opportunities that match a saved filter. Open the Search Alerts page in the sidebar, save at least one NAICS + optional keyword + state filter. Without one, the agent has nothing to check.",
  },
  {
    Icon: Building2,
    tone: "indigo",
    title: "2. Fill in Company + Past Performance",
    body: "The AI match score uses your Company NAICS codes and Past Performance records. If those are empty, every opportunity comes back as \"weak match\". 2-3 past projects is enough to start seeing real scores.",
  },
  {
    Icon: Sliders,
    tone: "violet",
    title: "3. Tune the sliders",
    body: "Daily proposal limit caps how many bids the agent processes per 24 hours (start with 3). Minimum match score filters noise — 70 is a good starting point. Auto-generate full proposals is a big-ticket switch — leave OFF until you trust the match scores.",
  },
  {
    Icon: Zap,
    tone: "emerald",
    title: "4. Enable + Run Now",
    body: "Click ENABLE AGENT (it goes green). Click RUN NOW to fire immediately, or leave it and the daily cron will process it overnight. Every cycle shows in RUN HISTORY.",
  },
  {
    Icon: CheckCircle2,
    tone: "fuchsia",
    title: "5. Watch credits (not surprise bills)",
    body: "Every processed opportunity uses 1 credit for the bid/no-bid check. If auto-generate is ON, add 3 credits per qualifying full proposal. Your credit chip in the sidebar always shows the remaining balance and turns amber/rose when you're running low.",
  },
];

const TONE_STYLES = {
  amber:   { bg: "bg-amber-100",   text: "text-amber-700",   ring: "ring-amber-200" },
  indigo:  { bg: "bg-indigo-100",  text: "text-indigo-700",  ring: "ring-indigo-200" },
  violet:  { bg: "bg-violet-100",  text: "text-violet-700",  ring: "ring-violet-200" },
  emerald: { bg: "bg-emerald-100", text: "text-emerald-700", ring: "ring-emerald-200" },
  fuchsia: { bg: "bg-fuchsia-100", text: "text-fuchsia-700", ring: "ring-fuchsia-200" },
};

function CreditsTable({ costs }) {
  const rows = [
    { label: "Bid/No-Bid analysis",                key: "bid_analysis",     credits: 1 },
    { label: "Full proposal (14 sections)",         key: "proposal",         credits: 3 },
    { label: "RFP Analyzer",                        key: "rfp_analysis",     credits: 1 },
    { label: "Estimator (WBS generate)",            key: "estimate",         credits: 1 },
    { label: "Estimate line-item suggest",          key: "estimate_line",    credits: 1 },
    { label: "Knowledge Q&A",                       key: "knowledge_qa",     credits: 1 },
    { label: "Outreach draft",                      key: "outreach_draft",   credits: 1 },
    { label: "Nudge draft",                         key: "nudge_draft",      credits: 1 },
    { label: "Auto-Agent · per opportunity checked",key: "autonomous_step",  credits: 1, note: "add 3 if auto-generating a full proposal" },
  ];
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="autoagent-credit-table">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-mono font-bold tracking-widest text-slate-500">CREDIT COSTS PER ACTION</div>
      <div className="divide-y divide-slate-100">
        {rows.map((r) => {
          const actual = costs?.[r.key] ?? r.credits;
          return (
            <div key={r.key} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <div>
                <div className="text-slate-800">{r.label}</div>
                {r.note && <div className="text-xs text-slate-500 mt-0.5">{r.note}</div>}
              </div>
              <div className="font-mono font-bold text-violet-700">{actual} {actual === 1 ? "credit" : "credits"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Two-part walkthrough for the Auto-Agent page:
 *  1) First-visit modal (dismissible; remembered in localStorage)
 *  2) Persistent HELP button that re-opens the same content as a side panel
 */
export default function AutoAgentTour({ costs }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) setModalOpen(true);
  }, []);

  const closeModal = () => {
    localStorage.setItem(STORAGE_KEY, "seen");
    setModalOpen(false);
  };
  const reopen = () => { setStep(0); setPanelOpen(true); };

  const StepIcon = STEPS[step].Icon;
  const stepTone = TONE_STYLES[STEPS[step].tone];

  return (
    <>
      {/* Persistent Help pill on the Auto-Agent page */}
      <button
        onClick={reopen}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono font-bold tracking-widest text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 transition-colors"
        data-testid="autoagent-help-btn"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        HOW IT WORKS
      </button>

      {/* First-time modal */}
      {modalOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-sm" data-testid="autoagent-tour-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center">
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-mono font-bold tracking-widest text-amber-700">WELCOME</div>
                  <div className="font-display font-bold text-lg text-slate-900">Auto-Agent in 60 seconds</div>
                </div>
              </div>
              <button onClick={closeModal} className="w-8 h-8 rounded-lg hover:bg-white/70 flex items-center justify-center text-slate-500 hover:text-rose-600" data-testid="autoagent-tour-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-6">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${stepTone.bg} ${stepTone.text} text-[10px] font-mono font-bold tracking-widest mb-3`}>
                STEP {step + 1} OF {STEPS.length}
              </div>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl ${stepTone.bg} ${stepTone.text} flex items-center justify-center shrink-0`}>
                  <StepIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl text-slate-900 mb-2">{STEPS[step].title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{STEPS[step].body}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-8">
                <div className="flex gap-1.5">
                  {STEPS.map((_, i) => (
                    <span key={i} className={`w-2 h-2 rounded-full transition-colors ${i === step ? "bg-violet-500" : i < step ? "bg-violet-300" : "bg-slate-200"}`} />
                  ))}
                </div>
                <div className="flex gap-2">
                  {step > 0 && (
                    <button onClick={() => setStep(step - 1)} className="px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1" data-testid="autoagent-tour-prev">
                      <ArrowLeft className="w-3.5 h-3.5" /> Back
                    </button>
                  )}
                  {step < STEPS.length - 1 ? (
                    <button onClick={() => setStep(step + 1)} className="bf-pill px-5 py-2 text-sm inline-flex items-center gap-1.5" data-testid="autoagent-tour-next">
                      Next <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button onClick={closeModal} className="bf-pill px-5 py-2 text-sm inline-flex items-center gap-1.5" data-testid="autoagent-tour-finish">
                      Got it <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Persistent side panel (re-opens all steps + credit table) */}
      {panelOpen && createPortal(
        <div className="fixed inset-0 z-50 flex" data-testid="autoagent-help-panel">
          <div className="flex-1 bg-slate-900/30 backdrop-blur-sm" onClick={() => setPanelOpen(false)} />
          <div className="w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-violet-600">HELP · AUTO-AGENT</div>
                <div className="font-display font-bold text-lg text-slate-900">How Auto-Agent works</div>
              </div>
              <button onClick={() => setPanelOpen(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600" data-testid="autoagent-help-panel-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {STEPS.map((s, i) => {
                const t = TONE_STYLES[s.tone];
                const Icon = s.Icon;
                return (
                  <div key={i} className="flex gap-4 items-start">
                    <div className={`w-10 h-10 rounded-xl ${t.bg} ${t.text} flex items-center justify-center shrink-0`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-sm text-slate-900 mb-1">{s.title}</div>
                      <div className="text-xs text-slate-600 leading-relaxed">{s.body}</div>
                    </div>
                  </div>
                );
              })}
              <CreditsTable costs={costs} />
              <div className="rounded-xl bg-violet-50 border border-violet-200 p-4 flex gap-3 items-start">
                <Info className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" />
                <div className="text-xs text-slate-700 leading-relaxed">
                  Prefer to ask a human? Tap the <b>Ask BidForge</b> chat bubble in the bottom corner — the AI help assistant answers questions about any tab, free.
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
